import { Component, HostListener, computed, inject } from '@angular/core';
import { AccessService } from '../../../core/services/access.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../icon/icon.component';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { AttachmentsComponent } from '../attachments/attachments.component';
import { OrderPreviewService } from '../../../core/services/order-preview.service';
import { PurchaseOrderService } from '../../../core/services/purchase-order.service';
import { SalesOrderService } from '../../../core/services/sales-order.service';
import { ProductService } from '../../../core/services/product.service';
import { VendorService } from '../../../core/services/vendor.service';
import { CustomerService } from '../../../core/services/customer.service';
import { WarehouseService } from '../../../core/services/warehouse.service';
import { OPEN_FULL_ORDER_STATE } from '../../../core/helpers/order-preview.guard';
import { MoneyPipe } from '../../../shared/pipes/money.pipe';

interface PreviewLine {
  key: string;
  product: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  isInventoryItem: boolean;
  /** Non-inventory PO lines: expense category. */
  category?: string;
  /** PO: lot generated on receipt. SO: lots the line draws from. */
  lots: { label: string; purchaseOrderId?: string }[];
  /** PO only: received / ordered. */
  received?: number;
}

interface PreviewField {
  label: string;
  value: string | null | undefined;
}

interface OrderPreview {
  kind: 'purchase-order' | 'sales-order';
  id: string;
  title: string;
  number: string;
  status: string;
  paymentStatus: string;
  flagged: boolean;
  flagReason?: string;
  flagNote?: string;
  partyLabel: string;
  partyName: string;
  partyLink: unknown[];
  fields: PreviewField[];
  lines: PreviewLine[];
  expensesTotal: number;
  total: number;
  notes?: string;
  fullPageLink: string[];
}

/**
 * Read-only preview of a purchase / sales order, opened from anywhere outside the Orders
 * module so the user doesn't lose the page they were on. "Open full order" goes to the
 * Orders module.
 */
@Component({
  selector: 'pp-order-preview-modal',
  standalone: true,
  imports: [MoneyPipe, CommonModule, RouterModule, IconComponent, StatusBadgeComponent, AttachmentsComponent],
  templateUrl: './order-preview-modal.component.html',
})
export class OrderPreviewModalComponent {
  protected readonly access = inject(AccessService);
  private readonly preview = inject(OrderPreviewService);
  private readonly router = inject(Router);
  private readonly purchaseOrders = toSignal(inject(PurchaseOrderService).getAll(), { initialValue: null });
  private readonly salesOrders = toSignal(inject(SalesOrderService).getAll(), { initialValue: null });
  private readonly products = toSignal(inject(ProductService).getAll(), { initialValue: [] });
  private readonly vendors = toSignal(inject(VendorService).getAll(), { initialValue: [] });
  private readonly customers = toSignal(inject(CustomerService).getAll(), { initialValue: [] });
  private readonly warehouses = toSignal(inject(WarehouseService).getAll(), { initialValue: [] });

  readonly target = this.preview.current;

  readonly loading = computed(() => {
    const target = this.target();
    if (!target) return false;
    return (target.type === 'purchase-order' ? this.purchaseOrders() : this.salesOrders()) === null;
  });

  readonly order = computed<OrderPreview | null>(() => {
    const target = this.target();
    if (!target) return null;
    return target.type === 'purchase-order' ? this.purchaseOrderPreview(target.id) : this.salesOrderPreview(target.id);
  });

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.target()) this.close();
  }

  close(): void {
    this.preview.close();
  }

  openFullOrder(order: OrderPreview): void {
    this.preview.close();
    this.router.navigate(order.fullPageLink, { state: OPEN_FULL_ORDER_STATE });
  }

  private productName(id: string): string {
    const product = this.products().find((p) => p.id === id);
    return product ? product.name : 'Unknown product';
  }

  private warehouseName(id?: string): string | undefined {
    return id ? this.warehouses().find((w) => w.id === id)?.name : undefined;
  }

  private purchaseOrderPreview(id: string): OrderPreview | null {
    const po = this.purchaseOrders()?.find((o) => o.id === id);
    if (!po) return null;

    return {
      kind: 'purchase-order',
      id: po.id,
      title: 'Purchase Order',
      number: po.number,
      status: po.status,
      paymentStatus: po.paymentStatus,
      flagged: po.flagged,
      flagReason: po.flagReason,
      flagNote: po.flagNote,
      partyLabel: 'Vendor',
      partyName: this.vendors().find((v) => v.id === po.vendorId)?.name ?? 'Unknown vendor',
      partyLink: ['/contacts/vendors', po.vendorId],
      fields: [
        { label: 'Order date', value: po.orderDate },
        { label: 'Expected date', value: po.expectedDate },
        { label: 'Received date', value: po.receivedDate },
        { label: 'Warehouse', value: this.warehouseName(po.warehouseId) },
        { label: 'Order type', value: po.orderType },
        { label: 'Buyer', value: po.buyerName },
        { label: 'Vendor invoice #', value: po.vendorInvoiceNumber },
        { label: 'BOL #', value: po.bolNumber },
      ],
      lines: po.items.map((item) => ({
        key: item.id,
        product: this.productName(item.productId),
        category: item.isInventoryItem ? undefined : item.category,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        total: item.total,
        isInventoryItem: item.isInventoryItem,
        lots: item.generatedLot ? [{ label: item.generatedLot.number }] : [],
        received: item.isInventoryItem ? item.receivedQuantity : undefined,
      })),
      expensesTotal: po.expensesTotal,
      total: po.total,
      notes: po.notes,
      fullPageLink: ['/orders/purchase-orders', po.id],
    };
  }

  private salesOrderPreview(id: string): OrderPreview | null {
    const so = this.salesOrders()?.find((o) => o.id === id);
    if (!so) return null;

    const destination =
      so.fulfillmentMethod === 'Pickup by customer' ? this.warehouseName(so.destinationWarehouseId) : so.destinationAddress;

    return {
      kind: 'sales-order',
      id: so.id,
      title: 'Sales Order',
      number: so.number,
      status: so.status,
      paymentStatus: so.paymentStatus,
      flagged: so.flagged,
      flagReason: so.flagReason,
      flagNote: so.flagNote,
      partyLabel: 'Customer',
      partyName: this.customers().find((c) => c.id === so.customerId)?.name ?? 'Unknown customer',
      partyLink: ['/contacts/customers', so.customerId],
      fields: [
        { label: 'Order date', value: so.orderDate },
        { label: 'Requested date', value: so.requestedDate },
        { label: 'Fulfilled date', value: so.fulfilledDate },
        { label: 'Fulfillment', value: so.fulfillmentMethod },
        { label: so.fulfillmentMethod === 'Pickup by customer' ? 'Pickup warehouse' : 'Ship to', value: destination },
        { label: 'Sales rep', value: so.salesRep },
        { label: 'Invoice #', value: so.soInvoiceNumber },
        { label: 'Customer PO #', value: so.customerPoNumber },
      ],
      lines: so.items.map((item) => ({
        key: item.id,
        product: this.productName(item.productId),
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        total: item.total,
        isInventoryItem: item.isInventoryItem,
        lots: item.assignedLots.map((lot) => ({
          label: `${lot.lotNumber} × ${lot.quantity}`,
          purchaseOrderId: lot.sourcePurchaseOrderId,
        })),
      })),
      expensesTotal: so.expensesTotal,
      total: so.total,
      notes: so.notes,
      fullPageLink: ['/orders/sales-orders', so.id],
    };
  }

  isDate(field: PreviewField): boolean {
    return /date$/i.test(field.label);
  }
}
